import _ from 'lodash';

import {
  IntegrationEnvironmentEnum,
  integrationEnvironmentOptions,
} from 'constants/loanActionEditor';

// HZ-4015: an integration package configures a base URL per environment. The Integration Studio
// package-detail endpoint returns them as baseUrlDev / baseUrlStaging / baseUrlProd; the list (summary)
// endpoint omits them, so a caller must hydrate the detail before validating env slots.
const envBaseUrlField = {
  [IntegrationEnvironmentEnum.DEV]: 'baseUrlDev',
  [IntegrationEnvironmentEnum.STAGING]: 'baseUrlStaging',
  [IntegrationEnvironmentEnum.PROD]: 'baseUrlProd',
};

// The per-env base URL fields only exist once the detail endpoint has hydrated the entity; a
// summary-only entity has none of them. Presence of the Dev key marks the detail as loaded.
const isPackageDetailLoaded = pkg => !_.isNil(pkg) && 'baseUrlDev' in pkg;

// An env slot is selectable only when the package has a base URL for it — otherwise a run would 404 /
// 500 with "no base URL configured". Until the detail hydrates, don't pre-disable (avoid a false block).
export const isEnvConfiguredOnPackage = (pkg, environment) => {
  if (!isPackageDetailLoaded(pkg)) {
    return true;
  }

  return !_.isEmpty(pkg[envBaseUrlField[environment]]);
};

// The display names of the environments this package has NO base URL for — used to tell the user WHY
// those env options are disabled. Empty until the detail hydrates.
const getUnconfiguredEnvNames = pkg =>
  isPackageDetailLoaded(pkg)
    ? integrationEnvironmentOptions
        .filter(({ value }) => !isEnvConfiguredOnPackage(pkg, value))
        .map(({ name }) => name)
    : [];

// AutocompleteCDS option accessors for the Vendor Environment selector, shared by every surface that
// renders it so the label/equality logic can't drift between them.
export const getEnvironmentOptionLabel = option => option.name;
export const isEnvironmentOptionEqual = (option, value) => option.value === value?.value;

// One-line helper for compact layouts (e.g. the Test Console, whose adjacent button row leaves no
// room for the wrapped multi-line variant — CDS renders helper text absolutely positioned).
export const ENV_HELPER_BASE = 'Leave empty to inherit the deployment environment.';

// Base helper text plus, when a package with unconfigured env slots is selected, the reason those
// options are greyed out. May wrap to two lines — use only where there is vertical room below the field.
export const getEnvHelperText = pkg => {
  const unconfigured = getUnconfiguredEnvNames(pkg);

  return unconfigured.length
    ? `${ENV_HELPER_BASE} ${unconfigured.join(', ')} not configured on this package.`
    : ENV_HELPER_BASE;
};
